import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';

import { IOption } from '../components/SelectSync';
import { IConfigAgendaBasica } from '../pages/Agenda';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

export interface IPerfil {
  PerCodigo: number;
  PerNome: string;
  PerTipo: number;
  PerAtivo: boolean;
  PerIntegracao?: boolean;
  agendaPerfil?: IConfigAgendaBasica;
}

interface IOptionsPerfil {
  optionPerfis: IOption[];
}
interface IPerfis {
  perfis: IPerfil[];
}

interface PerfilContextData {
  loading: boolean;
  optionsPerfil: IOptionsPerfil;
  perfis: IPerfis;
  buscaPerfil(): Promise<void>;
  limparPerfil(): void;
}

const PerfilContext = createContext<PerfilContextData>({} as PerfilContextData);

const PerfilProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();
  const [perfis, setPerfis] = useState<IPerfis>(() => {
    const perfs = localStorage.getItem('@AgendaFacil:perfis');
    if (perfs) return JSON.parse(perfs);

    return {} as IPerfis;
  });

  const [optionsPerfil, setOptionsPerfil] = useState<IOptionsPerfil>(() => {
    const optionPerfis = localStorage.getItem('@AgendaFacil:optionsPerfil');
    if (optionPerfis) return JSON.parse(optionPerfis);

    return {} as IOptionsPerfil;
  });

  const buscaPerfil = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<[IPerfil[], number, number[]]>(
        `/perfis?limite=1000`,
      );

      const options: IOption[] = response.data[0].map((op: IPerfil) => ({
        value: op.PerCodigo.toString(),
        label: op.PerNome,
        isDisabled: !op.PerAtivo,
      }));

      const integracao = response.data[2];

      const perfisLocal = response.data[0].map(per => {
        return {
          ...per,
          PerIntegracao:
            integracao.find(it => it === per.PerCodigo) !== undefined,
        };
      });
      const localPerfis: IPerfis = {
        perfis: perfisLocal,
      };

      const localOptions: IOptionsPerfil = {
        optionPerfis: options,
      };

      localStorage.setItem('@AgendaFacil:perfis', JSON.stringify(localPerfis));
      localStorage.setItem(
        '@AgendaFacil:optionsPerfil',
        JSON.stringify(localOptions),
      );
      setPerfis(localPerfis);

      setOptionsPerfil(localOptions);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar perfis',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    async function load(): Promise<void> {
      const tokenLocal = localStorage.getItem('@AgendaFacil:token');
      if (tokenLocal) {
        buscaPerfil();
      }
    }
    load();
  }, [buscaPerfil]);

  const limparPerfil = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:optionsPerfil');
    localStorage.removeItem('@AgendaFacil:perfis');
    setPerfis({} as IPerfis);
    setOptionsPerfil({} as IOptionsPerfil);
  }, []);

  return (
    <PerfilContext.Provider
      value={{
        loading,
        perfis,
        optionsPerfil,
        buscaPerfil,
        limparPerfil,
      }}
    >
      {children}
    </PerfilContext.Provider>
  );
};

function usePerfil(): PerfilContextData {
  const context = useContext(PerfilContext);

  if (!context) {
    throw new Error('usePerfil deve ser usado com o Perfil Provider');
  }

  return context;
}

export { PerfilProvider, usePerfil };
